import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ClientSideRowModelModule, ValidationModule } from "ag-grid-community";
import {
  SmartPlanDetailsStyled,
  CancelSmartPlanButton,
  // EditButton,
} from "./styled";
import {
  CustomBreadcrumb,
  CurrencyLabel,
  InfoLabel,
  InfoValue,
  StatusBadge,
  ConfirmationModal,
  SnackMsg,
} from "../../components";
import {
  COMMON_PAYMENT_CONSTANTS,
  SMART_PLANS_CONSTANTS,
  ADMIN_TYPE,
} from "../../config/constants";
import { PLAN_STATUS } from "./constants";
import { ROUTES } from "../../config/routes";
// import { EditPlanDetailsModal } from "./EditPlanDetailsModal";
// import { EditPaymentScheduleModal } from "./EditPaymentScheduleModal";
import { SKIPLY_MERCHANT_ID } from "../../utils/Config";
import { Skeleton } from "@mui/material";
// import { PlainEditLogo } from "../../assets/icons";
import dayjs from "dayjs";
import { API_URL, GRID_COLUMN_EMPTY_ICONS } from "utils/Constants";
import { ApiService } from "utils/ApiService";

const SmartPlanDetails = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [planData, setPlanData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelErrorMsg, setCancelErrorMsg] = useState("");
  // const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  // const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  // const [selectedPayment, setSelectedPayment] = useState(null);
  // const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const merchantIdGlobal = useMemo(() => {
    const role = localStorage.getItem("role");
    if (role === ADMIN_TYPE.SUPER_ADMIN)
      return localStorage.getItem("globalMerchantId") || "";
    if (role === ADMIN_TYPE.SCHOOL_ADMIN)
      return localStorage.getItem("schoolAdminMerchantId") || "";
    return "";
  }, []);

  const isSuperAdmin = useMemo(
    () =>
      localStorage.getItem("role") === ADMIN_TYPE.SUPER_ADMIN &&
      merchantIdGlobal === SKIPLY_MERCHANT_ID,
    [merchantIdGlobal],
  );

  const fetchPlanDetail = useCallback(async () => {
    setIsLoading(true);
    try {
      const storedPlan = sessionStorage.getItem("selectedSmartPlan");
      if (storedPlan) {
        const parsed = JSON.parse(storedPlan);
        // Validate cached data belongs to this planId (guards against stale cache)
        if (String(parsed.id) === String(planId)) {
          setPlanData(parsed);
          return;
        }
      }
      // sessionStorage miss or planId mismatch (page refresh / direct URL).
      // No dedicated GET-by-ID endpoint — redirect to list with a message.
        sessionStorage.setItem("flashMsg", "Session expired. Please select a plan from the list.");
      navigate(ROUTES.SMART_PLANS, { replace: true });
    } catch (err) {
      navigate(ROUTES.SMART_PLANS, { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [planId, navigate]);

  useEffect(() => {
    fetchPlanDetail();
  }, [fetchPlanDetail]);

  const columnDefs = useMemo(
    () => [
      {
        field: "title",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_MONTH,
        valueFormatter: (params) => {
          const title = params.data?.title;
          const description = params.data?.description;
          if (title || description) {
            return [title, description].filter(Boolean).join(" ");
          }
          return "N/A";
        },
        resizable: true,
        sortable: true,
        unSortIcon: true,
        icons: GRID_COLUMN_EMPTY_ICONS,
        filter: false,
        flex: 1,
        minWidth: 150,
      },
      {
        field: "dueDate",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_DUE_DATE,
        valueFormatter: (params) => {
          return dateFormatter(params.value);
        },
        flex: 1,
        minWidth: 120,
      },
      {
        field: "amount",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_AMOUNT,
        flex: 1,
        minWidth: 100,
        cellRenderer: (params) => <CurrencyLabel amount={params.value ?? 0} />,
      },
      {
        field: "status",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_STATUS,
        flex: 1,
        minWidth: 100,
        cellRenderer: (params) => <StatusBadge status={params.value} />,
      },
      {
        field: "paymentInfo.rrn",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_RRN,
        flex: 1,
        minWidth: 140,
        valueFormatter: (params) => {
          return params.data?.paymentInfo?.rrn ?? "";
        },
      },
      {
        field: "paymentInfo.failureReason",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_FAILED_REASON,
        flex: 1,
        minWidth: 230,
        valueFormatter: (params) => {
          return params.data?.failureReason ?? "";
        },
      },
      // We can add an edit button here for Super Admins to edit overall plan details (like start/end date, frequency, etc.) if needed in the future. For now, it's commented out as per requirements.
      // {
      //   field: "actions",
      //   headerName: "Actions",
      //   flex: 0.5,
      //   minWidth: 90,
      //   hide: !isSuperAdmin,
      //   headerClass: "ag-header-cell-center",
      //   cellClass: "ag-cell-center",
      //   cellRenderer: (params) => {
      //     return (
      //       <button
      //         className="edit-action-button"
      //         onClick={() => {
      //           setSelectedPayment(params.data);
      //           setShowEditPaymentModal(true);
      //         }}
      //       >
      //         <PlainEditLogo />
      //       </button>
      //     );
      //   },
      // },
    ],
    [isSuperAdmin],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      resizable: false,
      suppressMovable: true,
    }),
    [],
  );

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  // We can add an edit button here for Super Admins to edit overall plan details (like start/end date, frequency, etc.) if needed in the future. For now, it's commented out as per requirements.

  // const handleEditPlanClick = () => {
  //   setShowEditPlanModal(true);
  // };

  // const handleSavePlanDetails = async (formData) => {
  //   setIsSaving(true);
  //   try {
  //     // TODO: Replace with actual API call
  //     // await ApiService.putRequest(API_URL.UPDATE_SMART_PLAN, {
  //     //   instructionId: planData.instructionId,
  //     //   ...formData,
  //     // });

  //     // Simulate API call
  //     await new Promise((resolve) => setTimeout(resolve, 1000));

  //     // Update local state
  //     setPlanData((prev) => ({
  //       ...prev,
  //       ...formData,
  //     }));

  //     setShowEditPlanModal(false);
  //     // TODO: Show success notification
  //   } catch (error) {
  //     console.error("Error updating plan details:", error);
  //     // TODO: Show error notification
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  // const handleSavePaymentSchedule = async (formData) => {
  //   setIsSaving(true);
  //   try {
  //     // TODO: Replace with actual API call
  //     // await ApiService.putRequest(API_URL.UPDATE_PAYMENT_SCHEDULE, {
  //     //   instructionId: planData.instructionId,
  //     //   month: selectedPayment.month,
  //     //   ...formData,
  //     // });

  //     // Simulate API call
  //     await new Promise((resolve) => setTimeout(resolve, 1000));

  //     // Update local state
  //     setPlanData((prev) => ({
  //       ...prev,
  //       paymentSchedule: prev.paymentSchedule.map((payment) =>
  //         payment.month === selectedPayment.month
  //           ? { ...payment, ...formData }
  //           : payment,
  //       ),
  //     }));

  //     setShowEditPaymentModal(false);
  //     setSelectedPayment(null);
  //     // TODO: Show success notification
  //   } catch (error) {
  //     console.error("Error updating payment schedule:", error);
  //     // TODO: Show error notification
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleCancelConfirm = async () => {
    setIsCancelling(true);
    try {
      const cancelUrl = `${API_URL.CANCEL_SMART_PLAN}/${planData.instructionId}`;
      const resp = await ApiService.putRequest(cancelUrl, {
        merchantId: merchantIdGlobal,
      });

      if (resp && resp.status && resp.status.code === "Y") {
        setShowCancelModal(false);
        const successMsg =
          resp.status.message || "Smart plan cancelled successfully";
        sessionStorage.setItem("flashMsg", successMsg);
          navigate(ROUTES.SMART_PLANS, { replace: true });
      } else {
        const errorMsg =
          (resp && resp.status && resp.status.message) ||
          "Failed to cancel smart plan. Please try again.";
        setCancelErrorMsg(errorMsg);
      }
    } catch (error) {
      setCancelErrorMsg(
        error?.message || "Failed to cancel smart plan. Please try again.",
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const dateFormatter = (date) => {
    if (!date) return "";
    const d = dayjs(date);
    return d.isValid() ? d.format("DD/MM/YYYY") : "";
  };

  if (isLoading) {
    return (
      <CustomBreadcrumb
        header="Smart plans / Loading..."
        currentPage="Loading Details..."
        isFromLandingPage={false}
        showBackButton={true}
        onBackClick={() => navigate(-1)}
        parentPage={SMART_PLANS_CONSTANTS.BREADCRUMB_SMART_PLANS}
        parentPageHref={ROUTES.SMART_PLANS}
      >
        <SmartPlanDetailsStyled>
          {/* Skeleton for combined section */}
          <div className="combined-section">
            <div className="section-part">
              <Skeleton
                variant="rectangular"
                width={150}
                height={24}
                style={{ marginBottom: "16px" }}
              />
              <Skeleton variant="rectangular" width="100%" height={200} />
            </div>
            <div className="section-part">
              <Skeleton
                variant="rectangular"
                width={150}
                height={24}
                style={{ marginBottom: "16px" }}
              />
              <Skeleton variant="rectangular" width="100%" height={150} />
            </div>
          </div>

          {/* Skeleton for payment section */}
          <div className="payment-section">
            <Skeleton
              variant="rectangular"
              width={200}
              height={24}
              style={{ marginBottom: "16px" }}
            />
            <Skeleton variant="rectangular" width="100%" height={400} />
          </div>
        </SmartPlanDetailsStyled>
      </CustomBreadcrumb>
    );
  }

  if (!planData) return null;

  const canCancelPlan =
    planData?.status?.toUpperCase() === PLAN_STATUS.ACTIVE && !isCancelling;

  const gradeValue = planData?.beneficiary?.grade
    ? ` / ${planData.beneficiary.grade}`
    : "";
  const breadcrumbLabel = `Smart plan${gradeValue}`;

  return (
    <CustomBreadcrumb
      header={breadcrumbLabel}
      currentPage={breadcrumbLabel}
      isFromLandingPage={false}
      showBackButton={true}
      onBackClick={() => navigate(-1)}
      parentPage={SMART_PLANS_CONSTANTS.BREADCRUMB_SMART_PLANS}
      parentPageHref={ROUTES.SMART_PLANS}
      rightContent={
        canCancelPlan ? (
          <CancelSmartPlanButton
            onClick={handleCancelClick}
            bsPrefix="cancel-plan-btn"
          >
            Cancel smart plan
          </CancelSmartPlanButton>
        ) : null
      }
    >
      <SmartPlanDetailsStyled>
        {/* Schedule Summary and Student Details - Combined Section */}
        <div className="combined-section">
          {/* Schedule Summary */}
          <div className="section-part">
            <div className="section-header">
              <h3 className="section-title">
                {SMART_PLANS_CONSTANTS.DETAIL_SCHEDULE_SUMMARY}
              </h3>
              {/* 
              We can add an edit button here for Super Admins to edit overall plan details (like start/end date, frequency, etc.) if needed in the future. For now, it's commented out as per requirements.
              {isSuperAdmin && (
                <EditButton onClick={handleEditPlanClick}>
                  <PlainEditLogo />
                  Edit
                </EditButton>
              )} */}
            </div>
            <div className="info-grid">
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_INSTRUCTION_ID}</InfoLabel>
                <InfoValue>{planData?.instructionId}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_STATUS}</InfoLabel>
                <InfoValue>
                  <StatusBadge status={planData?.status} />
                </InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_TOTAL_PAYMENTS}</InfoLabel>
                <InfoValue>{planData?.totalPayments}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_LAST_PAYMENT_STATUS}</InfoLabel>
                <InfoValue>
                  <StatusBadge status={planData?.lastPaymentStatus} />
                </InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_DATE_CREATED}</InfoLabel>
                <InfoValue>{dateFormatter(planData?.createdAt)}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_START_DATE}</InfoLabel>
                <InfoValue>{dateFormatter(planData?.startDate)}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_END_DATE}</InfoLabel>
                <InfoValue>{dateFormatter(planData?.endDate)}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_NEXT_PAYMENT_DATE}</InfoLabel>
                <InfoValue>
                  {dateFormatter(planData?.nextPaymentDate)}
                </InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_TOTAL_AMOUNT}</InfoLabel>
                <InfoValue>
                  <CurrencyLabel amount={planData?.totalAmount ?? 0} />
                </InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_AMOUNT_PAID}</InfoLabel>
                <InfoValue>
                  <CurrencyLabel
                    amount={planData?.paymentSummary?.totalAmountPaid ?? 0}
                  />
                </InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_AMOUNT_DUE}</InfoLabel>
                <InfoValue>
                  <CurrencyLabel
                    amount={planData?.paymentSummary?.totalAmountDue ?? 0}
                  />
                </InfoValue>
              </div>
            </div>
          </div>

          {/* Student Details */}
          <div className="section-part">
            <h3 className="section-title">
              {SMART_PLANS_CONSTANTS.DETAIL_STUDENT_DETAILS}
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_STUDENT_NAME}</InfoLabel>
                <InfoValue>{planData?.beneficiary?.name}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_STUDENT_ID}</InfoLabel>
                <InfoValue>{planData?.beneficiary?.studentId}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_GRADE}</InfoLabel>
                <InfoValue>{planData?.beneficiary?.grade}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{SMART_PLANS_CONSTANTS.DETAIL_PARENT_EMAIL}</InfoLabel>
                <InfoValue>{planData?.userId}</InfoValue>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Schedule Section - Separate */}
        {planData?.paymentSchedule && planData?.paymentSchedule.length > 0 && (
          <div className="payment-section">
            <h3 className="section-title">
              {SMART_PLANS_CONSTANTS.DETAIL_PAYMENT_SCHEDULE}
            </h3>
            <div className="ag-grid-container">
              <div
                className="ag-theme-material"
                style={{ height: "600px", width: "100%" }}
              >
                <AgGridReact
                  rowData={planData?.paymentSchedule}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  suppressCellFocus={true}
                  suppressPaginationPanel={true}
                  rowHeight={52}
                  headerHeight={42}
                  animateRows={false}
                  modules={[ClientSideRowModelModule, ValidationModule]}
                />
              </div>
            </div>
          </div>
        )}

        <ConfirmationModal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          onConfirm={handleCancelConfirm}
          title={SMART_PLANS_CONSTANTS.CANCEL_PLAN_TITLE}
          // message={`${SMART_PLANS_CONSTANTS.CANCEL_PLAN_MESSAGE} ${planData?.instructionId}?`}
          message={`${SMART_PLANS_CONSTANTS.CANCEL_PLAN_MESSAGE}`}
          confirmLabel={SMART_PLANS_CONSTANTS.CANCEL_PLAN_CONFIRM}
          cancelLabel={SMART_PLANS_CONSTANTS.CANCEL_PLAN_BACK}
          isLoading={isCancelling}
        />

        {cancelErrorMsg ? (
          <SnackMsg
            msg={cancelErrorMsg}
            show={true}
            onClose={() => setCancelErrorMsg("")}
          />
        ) : null}

        {/* 
        We can add an edit button here for Super Admins to edit overall plan details (like start/end date, frequency, etc.) if needed in the future. For now, it's commented out as per requirements.
        {isSuperAdmin && (
          <>
            <EditPlanDetailsModal
              show={showEditPlanModal}
              onHide={() => setShowEditPlanModal(false)}
              onSave={handleSavePlanDetails}
              planData={planData}
              isLoading={isSaving}
            />

            <EditPaymentScheduleModal
              show={showEditPaymentModal}
              onHide={() => {
                setShowEditPaymentModal(false);
                setSelectedPayment(null);
              }}
              onSave={handleSavePaymentSchedule}
              paymentData={selectedPayment}
              isLoading={isSaving}
            />
          </>
        )} */}
      </SmartPlanDetailsStyled>
    </CustomBreadcrumb>
  );
};

export default SmartPlanDetails;
